
import React from "react";
import { Movie } from "@/types/movie";
import { Button } from "@/components/ui/button";
import { Undo } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Muted, Subtle, Span } from "@/components/ui/typography";
import MovieTags from "../MovieTags";
import { UNDO_DURATION } from "@/context/movie/movieUtils";
import { getPosterUrl } from "@/utils/imageUtils";

interface UndoCardListProps {
  movie: Movie;
  getActionText: () => string;
  handleUndo: (e: React.MouseEvent) => void;
  progress: number;
  isExpiring: boolean;
}

const UndoCardList = ({
  movie,
  getActionText,
  handleUndo,
  progress,
  isExpiring
}: UndoCardListProps) => {
  // Destructure properties we need from the movie object - using camelCase fields
  const { title, year, tags, director, overview } = movie;
  
  // Get poster URL using the utility function
  const posterUrl = getPosterUrl(movie);
  
  // If this card is in the process of expiring, add a special class
  const expiringClass = isExpiring ? 'opacity-80 scale-95' : '';

  return (
    <Card className={`transition-all duration-300 bg-mono-eerieBlack border-0 max-w-2xl ${expiringClass}`}>
      <CardContent className="p-2 md:p-3 flex items-center relative">
        <div className="w-12 h-16 flex-shrink-0 mr-3 md:mr-4 overflow-hidden rounded bg-mono-slateGray/20">
          {posterUrl ? (
            <img 
              src={posterUrl} 
              alt={title} 
              className="w-full h-full object-cover opacity-50"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = '/placeholder.svg';
              }}
            />
          ) : (
            <div className="w-full h-full bg-mono-slateGray/20 flex items-center justify-center">
              <Span size="xs" weight="bold" className="truncate px-1 text-mono-outerSpace">{title}</Span>
            </div>
          )}
        </div>
        
        <div className="flex-grow">
          <Muted weight="medium">{title}</Muted>
          <Subtle>{year}</Subtle>
          <MovieTags tags={tags} className="mt-1" maxTags={2} />
        </div>
        
        <div className="flex-shrink-0 ml-1 md:ml-2 flex flex-col items-end">
          <Span size="xs" weight="medium" variant="muted" className="italic mb-2 text-right">
            {getActionText()}
          </Span>
          <Button 
            onClick={handleUndo} 
            variant="listButtonUndo"
            size="badge"
            title="Undo"
          >
            <Undo />
            <span>Undo</span>
          </Button>
        </div>
        
        {/* Progress bar showing time remaining - CSS animated */}
        <div className="absolute bottom-0 left-0 right-0 h-1">
          <Progress 
            value={progress} 
            className="h-1 rounded-none bg-mono-outerSpace/20" 
            indicatorClassName="bg-mono-outerSpace/60" 
            transitionDuration={UNDO_DURATION}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default UndoCardList;
